import { Permissions } from "utils/permissions/permissionConstants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { tooltips, USER_API, budgetNonEditableReasons } from "config";
import { BudgetCategory, BudgetContingency, BudgetI } from "./types";
import {
  formatBEDateToMediumDateString,
  getToastMessageForError,
  handleToast,
  buildExportFilename,
  ServerError,
} from "utils";
import { isAxiosError } from "axios";
import { useReactToPrint } from "react-to-print";
import { CSVLink } from "react-csv";
import { BudgetPrint } from "./print/BudgetPrint";
import { AgGridReact } from "ag-grid-react";
import { getBudgetContingencyFromGrid, getLockedSubCategoriesFromGrid } from "./BudgetItemUtils";
import { buildBudgetCsvRows } from "./budgetCsvExport";
import { useAlertDialog } from "context/AlertDialogContext";
import { useDeviceDetect } from "hooks/useDeviceDetect";
import { FaFileCsv, FaFilePdf } from "react-icons/fa6";
import { CURRENCIES, toastMsg } from "config";
import { useAxios } from "hooks/useAxios";
import FileExcel from "assets/images/fileXlsx.svg?react";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { MoreActionsMenu } from "components/common/MoreActionsMenu";
import type { MoreActionsMenuGroup } from "components/common/MoreActionsMenu";
import { useBudgetApi } from "hooks/useBudget";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import {
  Coins,
  Download,
  FileCode,
  GitCompare,
  Info,
  LayoutList,
  LayoutTemplate,
  ListTree,
  Lock,
  LockOpen,
  MoreHorizontal,
  SlidersHorizontal,
  Stars,
  Upload,
} from "lucide-react";
import EditBudgetAssumptionsModal from "./EditBudgetAssumptionsModal";
import { useModalDialogCounter } from "context/ModalDialogCounterContext";
import { useRebudgetGate } from "./useRebudgetGate";
import { useTrackExport } from "hooks/useTrackExport";
import { BudgetAssumptionsDialog, BudgetDetailsDialog } from "./BudgetSummary";
import { BudgetScenarioCompareModal } from "./BudgetScenarioCompareModal";
import { BudgetTemplatePickerModal } from "./BudgetTemplatePickerModal";
import { BudgetImportModal } from "./BudgetImportModal";
import { BenchmarkVerdict } from "./BudgetBreakdown/BenchmarkVerdict";
import type { BenchmarkComparison, BenchmarkSet, BenchmarkSource } from "./budgetBenchmarks";
import type { BudgetBenchmarkMeta } from "./budgetBenchmarkDetails";
import type { BenchmarkClassificationAssessment } from "./budgetBucketClassification";
import type { BudgetTopSheetViewMode } from "./budgetTopSheetViewMode";
import { saveBlobAsFile } from "utils/saveBlobAsFile";

interface BudgetActionsProps {
  taxIncentiveAction?: React.ReactNode;
  sheetData: BudgetCategory[];
  budgetDetail?: Omit<BudgetI, "categories">;
  budgetContingency: BudgetContingency | null;
  gridRef: React.RefObject<AgGridReact<any>> | null;
  currency: string;
  updateBudgetCurrency: (currency: string) => void;
  benchmarkComparison?: BenchmarkComparison | null;
  benchmarkClassification?: BenchmarkClassificationAssessment | null;
  benchmarkSource?: BenchmarkSource;
  onBenchmarkSourceChange?: (source: BenchmarkSource) => void;
  peerSampleSize?: number | null;
  peerBenchmarkAvailable?: boolean;
  benchmarkMeta?: BudgetBenchmarkMeta;
  activeBenchmarkSet?: BenchmarkSet;
  budgetLocked?: boolean;
  onToggleBudgetLock?: () => void;
  isBudgetLockPending?: boolean;
  isTopSheetView?: boolean;
  topSheetViewMode?: BudgetTopSheetViewMode;
  onTopSheetViewModeChange?: (viewMode: BudgetTopSheetViewMode) => void;
  /** Controls the full rerun (assumptions) dialog, so the page can open it from elsewhere too. */
  isEditAssumptionsOpen?: boolean;
  onEditAssumptionsOpenChange?: (open: boolean) => void;
}

export const BudgetActions: React.FC<BudgetActionsProps> = ({
  taxIncentiveAction,
  sheetData,
  budgetDetail,
  budgetContingency,
  gridRef,
  currency,
  updateBudgetCurrency,
  benchmarkComparison,
  benchmarkClassification,
  benchmarkSource = "industry",
  onBenchmarkSourceChange,
  peerSampleSize,
  peerBenchmarkAvailable = false,
  benchmarkMeta,
  activeBenchmarkSet,
  budgetLocked = false,
  onToggleBudgetLock,
  isBudgetLockPending = false,
  isTopSheetView = false,
  topSheetViewMode = "outline",
  onTopSheetViewModeChange,
  isEditAssumptionsOpen,
  onEditAssumptionsOpenChange,
}) => {
  const { projectId, documentId, project } = useProjectIdentifiers();
  const { increment, decrement } = useModalDialogCounter();
  const [localEditAssumptionsOpen, setLocalEditAssumptionsOpen] = useState(false);
  const showEditAssumptions = isEditAssumptionsOpen ?? localEditAssumptionsOpen;
  const setShowEditAssumptions = onEditAssumptionsOpenChange ?? setLocalEditAssumptionsOpen;
  // Counted while open, whichever control opened it.
  useEffect(() => {
    if (!showEditAssumptions) return;
    increment();
    return decrement;
  }, [showEditAssumptions, increment, decrement]);
  const [showDetails, setShowDetails] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showScenarioCompare, setShowScenarioCompare] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const {
    block: rebudgetBlock,
    isViewer,
    isTrialProject,
    isFreePlanBlocked,
    isBudgetAiRunning,
  } = useRebudgetGate(budgetLocked);

  const handleOpenEditAssumptionsModal = () => {
    setShowEditAssumptions(true);
  };

  const handleCloseEditAssumptionsModal = () => {
    setShowEditAssumptions(false);
  };

  const handleOpenTemplatePicker = () => {
    setShowTemplatePicker(true);
    increment();
  };

  const handleCloseTemplatePicker = () => {
    setShowTemplatePicker(false);
    decrement();
  };

  const { canProject } = useProjectAuth(projectId);
  const canLockBudget = canProject(Permissions.PROJECT_BUDGET_LOCK);
  const axios = useAxios({ allResponse: true });
  const projectName = project?.name || "";
  const document_title = buildExportFilename({
    projectName,
    exportType: "budget",
  });
  const budgetRef = useRef(null);
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const { browserType } = useDeviceDetect();
  const { showAlertDialog } = useAlertDialog();
  const { budgetId } = useBudgetApi(true);
  const trackExport = useTrackExport();

  const currencySymbol =
    CURRENCIES.find((cur) => cur.abbreviation === currency.toUpperCase())?.symbol ?? "$";

  const handleCsvExport = () => {
    csvLinkRef.current?.link?.click?.();
    trackExport({ projectId, documentId, exportType: "budget", format: "csv" });
  };

  const isRebudgetDisabled = rebudgetBlock !== null;
  const rebudgetDisabledTitle = rebudgetBlock?.message;

  const isTemplatesDisabled = isViewer || budgetLocked || isBudgetAiRunning;

  const templatesDisabledTitle = budgetLocked
    ? budgetNonEditableReasons.budgetLocked
    : isViewer
      ? "You are a viewer. Ask the owner to upgrade your role."
      : isBudgetAiRunning
        ? "Unavailable while AI budget is running."
        : undefined;

  const handleExport = () => {
    if (browserType === "Safari") {
      showAlertDialog({
        title: "Export Recommendation",
        message:
          "You are using Safari browser. Please note that the export functionality might not work as expected. We recommend using Chrome or Firefox for better experience.",
        type: "warning",
        actions: {
          success: { text: "Export", onSuccess: handlePrint },
          cancel: "Close",
        },
      });
    } else {
      handlePrint();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => budgetRef.current,
    documentTitle: document_title + ".pdf",
    copyStyles: true,
    onAfterPrint: () => {
      trackExport({ projectId, documentId, exportType: "budget", format: "pdf" });
    },
  });

  const getCSVExportData = () => {
    const budgetContingency: BudgetContingency | null = gridRef?.current?.api
      ? getBudgetContingencyFromGrid(gridRef?.current?.api)
      : null;
    return buildBudgetCsvRows(sheetData, budgetContingency, currency);
  };

  const handleXlsxExport = async () => {
    if (!projectId || !documentId || !budgetId) return;
    try {
      const response = await axios.get(
        USER_API.exportBudgetXlsx(projectId.toString(), documentId.toString(), budgetId.toString()),
        {
          params: {
            format: "xlsx",
          },
          responseType: "blob",
        }
      );
      saveBlobAsFile(new Blob([response.data]), `${document_title}.xlsx`);
      trackExport({ projectId, documentId, exportType: "budget", format: "xlsx" });
    } catch {
      handleToast(toastMsg.genericServerErr, "error");
    }
  };

  const handleBudgetXmlExport = async () => {
    if (!projectId || !documentId || !budgetId) return;
    try {
      const response = await axios.get(
        USER_API.exportBudgetMmbx(projectId.toString(), documentId.toString(), budgetId.toString()),
        { responseType: "blob" }
      );
      saveBlobAsFile(new Blob([response.data]), `${document_title}.xml`);
      trackExport({ projectId, documentId, exportType: "budget", format: "rivetai_xml" });
    } catch (error) {
      const serverError = isAxiosError(error) ? ServerError.fromAxiosError(error) : null;
      handleToast(
        serverError ? getToastMessageForError(serverError) : toastMsg.genericServerErr,
        "error"
      );
    }
  };

  const handleCurrencyChange = (nextCurrency: string) => {
    updateBudgetCurrency(nextCurrency);
  };

  const moreActionsGroups = useMemo((): MoreActionsMenuGroup[] => {
    const groups: MoreActionsMenuGroup[] = [
      {
        id: "budget",
        items: [
          {
            id: "details",
            label: "Details",
            icon: <Info />,
            onSelect: () => setShowDetails(true),
          },
          {
            id: "assumptions",
            label: "Assumptions",
            icon: <SlidersHorizontal />,
            disabled: budgetLocked,
            title: budgetLocked ? budgetNonEditableReasons.budgetLocked : undefined,
            onSelect: () => setShowAssumptions(true),
          },
          {
            id: "scenarios",
            label: "Scenarios",
            icon: <GitCompare />,
            title: isViewer
              ? "Compare saved estimate scenarios (read-only)."
              : "Save and compare estimate scenarios.",
            onSelect: () => setShowScenarioCompare(true),
          },
          {
            id: "templates",
            label: "Templates",
            icon: <LayoutTemplate />,
            disabled: isTemplatesDisabled,
            title: templatesDisabledTitle,
            onSelect: handleOpenTemplatePicker,
          },
        ],
      },
    ];

    if (isTopSheetView && onTopSheetViewModeChange) {
      groups.push({
        id: "view",
        label: "View",
        items: [
          {
            id: "topsheet-layout",
            label: "Top sheet layout",
            icon: topSheetViewMode === "classic" ? <LayoutList /> : <ListTree />,
            children: [
              {
                id: "outline",
                label: "Outline",
                icon: <ListTree />,
                title: "Bucket header first, accounts indented below.",
                selected: topSheetViewMode === "outline",
                onSelect: () => onTopSheetViewModeChange("outline"),
              },
              {
                id: "classic",
                label: "Classic",
                icon: <LayoutList />,
                title: "Account rows first, bucket subtotal below when expanded.",
                selected: topSheetViewMode === "classic",
                onSelect: () => onTopSheetViewModeChange("classic"),
              },
            ],
          },
        ],
      });
    }

    groups.push(
      {
        id: "workspace",
        label: "Workspace",
        items: [
          {
            id: "budget-lock",
            label: budgetLocked ? "Unlock estimate" : "Lock estimate",
            icon: budgetLocked ? <LockOpen /> : <Lock />,
            disabled: !canLockBudget || isBudgetLockPending,
            title: !canLockBudget
              ? "You don't have permission to lock this estimate."
              : budgetLocked
                ? "Unlock to edit structure, rates, and assumptions. Actuals can still be entered while locked."
                : "Lock the estimate for handoff/review. Actuals entry stays available.",
            onSelect: () => onToggleBudgetLock?.(),
          },
          {
            id: "currency",
            label: `Currency: ${currency}`,
            icon: <Coins />,
            disabled: budgetLocked,
            title: budgetLocked ? budgetNonEditableReasons.budgetLocked : undefined,
            submenuContentClassName: "max-h-64 overflow-y-auto",
            children: CURRENCIES.map((currencyOption) => ({
              id: currencyOption.abbreviation,
              label: currencyOption.abbreviation,
              icon: <currencyOption.icon />,
              disabled: budgetLocked,
              title: budgetLocked ? budgetNonEditableReasons.budgetLocked : undefined,
              onSelect: () => handleCurrencyChange(currencyOption.abbreviation),
            })),
          },
        ],
      },
      {
        id: "files",
        label: "Import & export",
        items: [
          {
            id: "import-budget",
            label: "Import budget",
            icon: <Download />,
            permission: Permissions.PROJECT_UPDATE,
            disabled: budgetLocked || isViewer,
            title: budgetLocked
              ? budgetNonEditableReasons.budgetLocked
              : isViewer
                ? "You are a viewer. Ask the owner to upgrade your role."
                : "Import budget XML or JSON as a new version.",
            onSelect: () => setShowImportModal(true),
          },
          {
            id: "export",
            label: "Export",
            icon: <Upload />,
            permission: Permissions.PROJECT_EXPORT,
            children: [
              {
                id: "pdf",
                label: "PDF",
                icon: <FaFilePdf />,
                onSelect: handleExport,
              },
              {
                id: "csv",
                label: "CSV",
                icon: <FaFileCsv />,
                onSelect: handleCsvExport,
              },
              {
                id: "xlsx",
                label: "XLSX",
                icon: <FileExcel />,
                onSelect: handleXlsxExport,
              },
              {
                id: "budget-xml",
                label: "RivetAI Budget XML",
                icon: <FileCode />,
                title:
                  "RivetAI budget backup for re-import into RivetAI. Does not open in Movie Magic Budgeting.",
                onSelect: handleBudgetXmlExport,
              },
            ],
          },
        ],
      }
    );

    return groups;
  }, [
    budgetLocked,
    canLockBudget,
    currency,
    handleBudgetXmlExport,
    handleCsvExport,
    handleExport,
    handleOpenTemplatePicker,
    handleXlsxExport,
    isBudgetLockPending,
    isTemplatesDisabled,
    isTopSheetView,
    isViewer,
    onToggleBudgetLock,
    onTopSheetViewModeChange,
    templatesDisabledTitle,
    topSheetViewMode,
  ]);

  const moreActionsTriggerIcon = budgetLocked ? (
    <span className="relative inline-flex">
      <MoreHorizontal />
      <Lock
        className="pointer-events-none absolute -right-1 -top-1 !size-[10px] text-primary"
        strokeWidth={2.25}
        aria-hidden
      />
    </span>
  ) : undefined;

  const moreActionsTitle = budgetLocked ? budgetNonEditableReasons.budgetLocked : undefined;

  return (
    <>
      <EditBudgetAssumptionsModal
        open={showEditAssumptions}
        setOpen={handleCloseEditAssumptionsModal}
        lockedCategories={
          gridRef?.current?.api ? getLockedSubCategoriesFromGrid(gridRef.current.api) : []
        }
        sheetData={sheetData}
        budgetDetail={budgetDetail}
        handleUpdateBudgetCurrency={updateBudgetCurrency}
      />
      <BudgetDetailsDialog
        budgetDetail={budgetDetail}
        open={showDetails}
        onOpenChange={setShowDetails}
        showTrigger={false}
      />
      <BudgetAssumptionsDialog
        budgetDetail={budgetDetail}
        open={showAssumptions}
        onOpenChange={setShowAssumptions}
        showTrigger={false}
      />
      <BudgetTemplatePickerModal
        open={showTemplatePicker}
        onClose={handleCloseTemplatePicker}
        budgetDetail={budgetDetail}
        budgetId={budgetId}
      />
      <BudgetImportModal open={showImportModal} onClose={() => setShowImportModal(false)} />
      <BudgetScenarioCompareModal
        open={showScenarioCompare}
        onClose={() => setShowScenarioCompare(false)}
        currencySymbol={currencySymbol}
      />
      <div
        className="flex flex-wrap items-center justify-end gap-2 text-nowrap"
        role="group"
        aria-label="Budget actions"
      >
        {benchmarkComparison && benchmarkMeta ? (
          <BenchmarkVerdict
            comparison={benchmarkComparison}
            classificationAssessment={benchmarkClassification}
            source={benchmarkSource}
            onSourceChange={onBenchmarkSourceChange}
            peerSampleSize={peerSampleSize}
            peerBenchmarkAvailable={peerBenchmarkAvailable}
            meta={benchmarkMeta}
            benchmarkSet={activeBenchmarkSet}
          />
        ) : null}

        {taxIncentiveAction}

        <Tooltip>
          <TooltipTrigger>
            <div>
              <Button
                size="sm"
                variant="ghostBrand"
                aria-label="AI Rebudget"
                title={rebudgetDisabledTitle ?? "AI Rebudget"}
                disabled={isRebudgetDisabled}
                onClick={() => !isRebudgetDisabled && handleOpenEditAssumptionsModal()}
              >
                <Stars />
                Rebudget
              </Button>
            </div>
          </TooltipTrigger>
          {isTrialProject ? (
            <TooltipContent>
              Rebudgeting is not available for trial projects. Purchase a license to unlock this
              feature.
            </TooltipContent>
          ) : isFreePlanBlocked ? (
            <TooltipContent>{tooltips.freePlan.upgradeSubscription}</TooltipContent>
          ) : isViewer ? (
            <TooltipContent>You are a viewer. Ask the owner to upgrade your role.</TooltipContent>
          ) : isBudgetAiRunning ? (
            <TooltipContent>Unavailable while AI budget is running.</TooltipContent>
          ) : budgetLocked ? (
            <TooltipContent>{budgetNonEditableReasons.budgetLocked}</TooltipContent>
          ) : null}
        </Tooltip>

        <MoreActionsMenu
          groups={moreActionsGroups}
          triggerIcon={moreActionsTriggerIcon}
          title={moreActionsTitle}
          modal={false}
        />
      </div>
      <CSVLink
        data={getCSVExportData()}
        filename={document_title + ".csv"}
        role="link"
        className="hidden"
        ref={csvLinkRef}
      />
      <div className="hidden">
        <div className="printable" ref={budgetRef}>
          <BudgetPrint
            data={sheetData}
            project={projectName + ""}
            createdAt={formatBEDateToMediumDateString(budgetDetail?.created_at)}
            updatedAt={formatBEDateToMediumDateString(budgetDetail?.updated_at)}
            budgetDetail={budgetDetail}
            budgetContingency={budgetContingency}
          />
        </div>
      </div>
    </>
  );
};
